import { Badge, Card, Group, Stack, Text, Title } from "@mantine/core";
import { IconArrowsShuffle, IconDatabase } from "@tabler/icons-react";
import {
  LuaBlock,
  PresetCallout,
  useStepRuleSet,
} from "./components/SharedRuleParts";
import {
  CrossStepRuleView,
  DATA_SOURCE_LABELS,
  SOURCE_RELATIVE_LABELS,
  USAGE_TYPE_COLORS,
  USAGE_TYPE_LABELS,
} from "./types";

const CROSS_STEP_SKELETON_HEIGHTS = [28, 56, 140, 140] as const;

const RuleCard = ({
  rule,
  index,
}: {
  rule: CrossStepRuleView;
  index: number;
}) => {
  const sourceLabel = rule.sourceRelative
    ? SOURCE_RELATIVE_LABELS[rule.sourceRelative]
    : rule.sourceStepId
      ? "Specific step"
      : "Unspecified source";

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <Badge variant="filled" color="gray" size="sm">
              #{index + 1}
            </Badge>
            <Badge
              variant="light"
              color="cyan"
              leftSection={<IconArrowsShuffle size={12} />}
            >
              {sourceLabel}
            </Badge>
            <Badge
              variant="light"
              color="indigo"
              leftSection={<IconDatabase size={12} />}
            >
              {DATA_SOURCE_LABELS[rule.dataSource]}
            </Badge>
          </Group>
          <Badge
            variant="filled"
            color={USAGE_TYPE_COLORS[rule.usage.type]}
            size="lg"
          >
            {USAGE_TYPE_LABELS[rule.usage.type]}
          </Badge>
        </Group>

        {rule.usage.type === "QUALIFY" && (
          <LuaBlock label="Qualify condition" value={rule.usage.condition} />
        )}

        {rule.usage.type === "INJECT_SCORE" && (
          <LuaBlock label="Score formula" value={rule.usage.formula} />
        )}

        {rule.usage.type === "SEED_ORDER" && (
          <Text size="xs" c="dimmed" fs="italic">
            Seeds teams using the source step's ordering — no Lua expression
            required.
          </Text>
        )}
      </Stack>
    </Card>
  );
};

export const CrossStepEditor = () => {
  const { ruleSet, fallback } = useStepRuleSet({
    title: "Cross-Step Rules",
    icon: <IconArrowsShuffle size={20} />,
    skeletonHeights: CROSS_STEP_SKELETON_HEIGHTS,
  });

  if (!ruleSet) return fallback;

  const rules = ruleSet.crossStepRules ?? [];

  return (
    <Stack>
      <Group gap="xs">
        <IconArrowsShuffle size={20} />
        <Title order={2}>Cross-Step Rules</Title>
        <Badge variant="light" size="lg">
          {rules.length}
        </Badge>
      </Group>

      <PresetCallout />

      {rules.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No cross-step rules configured
        </Text>
      ) : (
        <Stack gap="md">
          {rules.map((rule, idx) => (
            <RuleCard
              key={`${rule.sourceStepId ?? "rel"}-${rule.sourceRelative ?? "abs"}-${rule.usage.type}-${idx}`}
              rule={rule}
              index={idx}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
